<template>
  <modal :closeable="true" @abort="closeModal">
    <template>
      <h3>{{ isGracePeriod ? $t('titles.upgrade_membership') : $t('titles.new_terms') }}</h3>
    </template>
    <template>
      <template>
        <div v-html="termsText" class="terms-text mt-2"></div>
        <p class="mt-3" v-if="canSelect"><i>{{ $t('labels.select_membership_type') }}</i></p>
        <div v-if="membershipTypeUpgrades && membershipTypeUpgrades.length>0 && !selectedMembershipType">
          <template v-for="membershipTypeUpgrade in membershipTypeUpgrades">
            <MembershipType
                @open-checkout="selectMembershipTypeUpgrade(membershipTypeUpgrade)"
                :price-description="priceDescription"
                :coupon="membershipTypeUpgrade.newCoupon"
                button-label="Select"
                :button-enabled="true"
                :key="membershipTypeUpgrade.newMembershipType.id"
                :membership-type="membershipTypeUpgrade.newMembershipType"
                :show-trial="false"/>
          </template>
        </div>
        <div v-if="selectedMembershipTypeUpgrade">
          <MembershipType
              @open-checkout="selectMembershipTypeUpgrade(null)"
              :price-description="priceDescription"
              :coupon="selectedMembershipTypeUpgrade.newCoupon"
              button-label="Unselect"
              :button-visible="canSelect"
              :button-enabled="true"
              :key="selectedMembershipType.id"
              :membership-type="selectedMembershipType"
              :show-trial="false"/>

          <div v-if="!skipPaymentDetails">
            <StripeForm
                :token-trigger="tokenTrigger"
                :card-payment-details="cardPaymentDetails"
                :handle-card-payment-trigger="handleCardPaymentTrigger"
                @on-token="onStripeToken"
                @on-error="onStripeError"
                @on-handle-card-payment="onStripeHandleCardPayment"
            />
          </div>

        </div>
        <div class="terms mt-2">
          <p>
            <Checkbox id="tos_checkbox" v-model="termsAccepted" :invalid="termsNotAccepted">
              I accept
              <template v-if="privacyUrl"> the <a :href="privacyUrl" target="_blank" style="text-decoration: underline;">Privacy Policy</a> and </template>
              <a :href="termsUrl" target="_blank" style="text-decoration: underline">Terms of Use</a>
            </Checkbox>
          </p>
        </div>

        <div class="mt-2 button-wrapper">
          <SecondaryButton @click="closeModal">
            {{ $t('buttons.remind_me_later') }}
          </SecondaryButton>

          <PrimaryButton @click="upgrade" cta :disabled="!selectedMembershipType || loading">
            {{ loading ? $t('buttons.processing') : $t('buttons.accept') }}
          </PrimaryButton>

        </div>
      </template>
    </template>
  </modal>
</template>

<script>
import Modal from '@/components/modal/ModalRegular.vue'
import PrimaryButton from "../../buttons/PrimaryButton";
import {getDefaultPrivacyUrl, getDefaultTermsUrl, getRequiresActionError, getTermsUrl, handleError, resetError} from "../../../store/utils";
import Checkbox from "../../input/Checkbox";
import SecondaryButton from "../../buttons/SecondaryButton";
import {i18n} from "../../../lang/setup";
import markdown from "markdown";
import MembershipType from "./MembershipType";
import {getMetaValue, parseInviteCodesFromUri} from "../../../utils";
import {SET_INVITE_CODES} from "../../../store/mutationTypes";
import StripeForm from "../../input/StripeForm";

const actions = {
  create: 'create', // create new membership
  update: 'update', // upgrade immediately
  acceptUpgrade: 'acceptUpgrade', // upgrade will be done later
}

export default {
  data() {
    return {
      termsAccepted: false,
      termsNotAccepted: false,
      selectedMembershipTypeUpgrade: null,
      loading: false,

      tokenTrigger: 0,
      handleCardPaymentTrigger: 0,
      stripeToken: null,
      cardPaymentDetails: {}
    }
  },
  computed: {
    branch() {
      return process.env.FG_APP_BRANCH
    },

    currentUser() {
      return this.$store.getters.currentUser
    },

    isGracePeriod() {
      return !this.currentUser.upgradeNeeded && this.currentUser.isUpgradeGracePeriod;
    },
    action() {
      if (this.isGracePeriod) {
        return actions.create
      } else {
        if (this.currentUser.upgradeMethod == 'immediate') {
          return actions.update
        } else {
          return actions.acceptUpgrade
        }
      }
    },

    currentMembership() {
      if (this.currentUser.membership) {
        return this.currentUser.membership
      }
      return null;
    },

    membershipType: function () {
      if (this.currentMembership && this.currentMembership.membershipType) {
        return this.currentMembership.membershipType
      }
      if (this.$store.getters.selectedMembershipType) {
        return this.$store.getters.selectedMembershipType
      }
      return null;
    },
    newMembershipTypeId() {
      return this.membershipType.newMembershipType.id
    },
    zeroCost() {
      return this.selectedMembershipType && this.selectedMembershipType.price === 0
    },
    skipPaymentDetails() {
      if (this.isGracePeriod) {
        return false;
      }
      return this.currentUser.isStripeCustomer || this.zeroCost
    },
    newMembershipType() {
      return this.membershipType && this.membershipType.newMembershipType
    },
    membershipTypeUpgrades() {
      return this.currentUser && this.currentUser.membershipTypeUpgrades.data
    },
    privacyUrl() {
      return getDefaultPrivacyUrl();
    },
    termsUrl() {
      const url = this.membershipType ? getTermsUrl(this.membershipType) : getDefaultTermsUrl();
      if (!url.includes("?")) {
        // adding quotation mark to overcome caching
        return `${url}?`;
      } else {
        return url;
      }
    },
    termsText() {
      if (!this.membershipType) {
        return null;
      }
      const metaValue = getMetaValue(this.membershipType.metaValues, 'upgrade-terms');

      // const defaultTerms = `No text added. Add new meta value \`upgrade-terms\` with language \`${i18n.locale}\``;
      const defaultTerms = "";
      return markdown.markdown.toHTML(metaValue ? metaValue : defaultTerms);

    },
    selectedMembershipType() {
      return this.selectedMembershipTypeUpgrade && this.selectedMembershipTypeUpgrade.newMembershipType
    },
    canSelect() {
      return this.membershipTypeUpgrades.length > 1;
    },

    priceDescription() {
      if (this.currentUser && this.currentUser.upgradeMethod === 'delayed') {
        return 'The revised pricing will take effect with your next renewal';
      }
      return null;
    },
  },
  methods: {
    onStripeError(error) {
      this.loading = false;
      handleError(this.$store.commit, error)
    },
    onStripeToken(token) {
      this.stripeToken = token
      this.update()
    },
    completeMembership: function () {
      const {membership_id} = this.cardPaymentDetails;
      this.$store.dispatch('completeMembership', {id: membership_id})
          .then((response) => {
            this.loading = false;
            this.$store.dispatch('setHideUpgradeWall', true)
            // this.sendConversion(response);
            // this.$store.commit(SET_INVITE_CODES, null)
            this.$router.replace({path: '/upgrade/success'})
          })
          .catch((err) => {
            this.loading = false
            handleError(this.$store.commit, err)
          })
    },
    onStripeHandleCardPayment(result) {
      if (this.action == actions.acceptUpgrade) {
        // submit again
        this.update();
      } else {
        this.completeMembership();
      }
    },
    update: function () {
      let promise;
      switch (this.action) {
        case actions.create:
          promise = this.createMembershipRequest()
          break;
        case actions.update:
          promise = this.updateMembershipRequest()
          break;
        case actions.acceptUpgrade:
          promise = this.acceptUpgradeRequest()
          break;
      }
      promise.then((/*response*/) => {
        this.$store.dispatch('setHideUpgradeWall', true)
        this.loading = false;
        // this.sendConversion(response);
        this.$router.replace({path: '/upgrade/success'})
      }, (err) => {
        const requiresActionError = getRequiresActionError(err);
        if (requiresActionError) {
          resetError(this.$store.commit)
          this.cardPaymentDetails = requiresActionError.details
          this.handleCardPaymentTrigger++
        } else {
          this.loading = false;
          handleError(this.$store.commit, err)
        }
      })
    },

    upgrade() {
      resetError(this.$store.commit)
      this.termsNotAccepted =  !this.termsAccepted;
      if (!this.termsAccepted) {
        return false;
      }

      this.loading = true;
      if (this.skipPaymentDetails) {
        this.update()
      } else {
        this.tokenTrigger++
      }
    },

    selectMembershipTypeUpgrade(membershipTypeUpgrade) {
      this.selectedMembershipTypeUpgrade = membershipTypeUpgrade;
    },

    closeModal() {
      this.$store.dispatch('setHideUpgradeWall', true)
      this.$router.push(this.$route.query.redirect || '/')
    },



    createMembershipRequest: function () {
      const params = {
        stripeToken: this.stripeToken && this.stripeToken.id,
        stripeCoupon: this.selectedMembershipTypeUpgrade.newCoupon && this.selectedMembershipTypeUpgrade.newCoupon.code,
        // provider:  "stripe",
        membershipTypeId: this.selectedMembershipTypeUpgrade.newMembershipType.id,
        userId: this.currentUser.id,
        storeDiscount: 'default-discount',
        inviteCodes: this.$store.getters.inviteCodes,
      };
      return this.$store.dispatch('createMembership', params);
    },

    updateMembershipRequest: function () {
      const params = {
        stripeToken: this.stripeToken && this.stripeToken.id,
        stripeCoupon: this.selectedMembershipTypeUpgrade.newCoupon && this.selectedMembershipTypeUpgrade.newCoupon.code,
        // provider:  "stripe",
        membershipTypeId: this.selectedMembershipTypeUpgrade.newMembershipType.id,
        // userId: this.currentUser.id,
        storeDiscount: 'default-discount',
        inviteCodes: this.$store.getters.inviteCodes,
        id: this.currentMembership.id,
      };
      return this.$store.dispatch('updateMembership', params);
    },

    acceptUpgradeRequest: function () {
      const params = {
        stripeToken: this.stripeToken && this.stripeToken.id,
        // stripeCoupon: this.upgradeCouponCode,
        // membershipTypeId: this.upgradeMembershipTypeId,
        // userId: this.$store.getters.currentUser.id,
        membershipTypeUpgradeId: this.selectedMembershipTypeUpgrade.id,
        inviteCodes: this.$store.getters.inviteCodes,
        id: this.currentMembership.id,
      };
      return this.$store.dispatch('acceptUpgrade', params);
    },

    // upsertMembershipRequest() {
    //   if (this.currentMembership) {
    //     return this.updateMembershipRequest()
    //   } else {
    //     return this.createMembershipRequest()
    //   }
    // },
    loadPreviewMembershipType: function () {
      const membershipTypeId = 'membership_type_id' in this.$route.query ? this.$route.query.membership_type_id : null;
      if (membershipTypeId) {
        // if invite codes is passed as query parameter, lets set it
        if (this.$route.query.invite && this.$route.query.invite.length > 0) {
          const codes = parseInviteCodesFromUri(this.$route.query.invite)
          if (codes && codes.length > 0) {
            this.$store.commit(SET_INVITE_CODES, codes)
          }
        }
        this.$store.dispatch('getMembershipType', {
          id: membershipTypeId,
          inviteCodes: this.$store.getters.inviteCodes,
        })
      }
    },

  },
  mounted() {
    if ('preview' in this.$route.query) {
      this.loadPreviewMembershipType();
    }
  },

  watch: {
    termsAccepted() {
      this.termsNotAccepted = false;
    },
    membershipTypeUpgrades: {
      immediate: true,
      handler(newVal, oldVal) {
        if (this.membershipTypeUpgrades && this.membershipTypeUpgrades.length == 1) {
          this.selectedMembershipTypeUpgrade = this.membershipTypeUpgrades[0]
        }
      }
    }

  },

  components: {
    StripeForm,
    MembershipType,
    SecondaryButton,
    Checkbox,
    PrimaryButton,
    Modal,

  }
}
</script>

<style lang="scss" scoped>

.terms-text {
  font-size: $font-size-b2;
}

.button-wrapper {
  display: flex;
  justify-content: flex-end;
  @media screen and (max-width: 750px) {

  }
  button {
    margin-left: 20px;
  }
}

.terms-text {
  //font-size: 16px;
  //padding: 20px;
  max-width: 520px;
}

</style>

